import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Table chrome: filter tabs, search, pagination.
 *
 * Pagination is the point. Every page in this panel fetched its entire table
 * — `select('*')` with no limit — and filtered in the browser. That is fine at
 * fifty rows and catastrophic at fifty thousand, and it also meant search only
 * ever searched the rows that happened to be loaded.
 */

export function FilterTabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: { key: T; label: string; count?: number; tone?: string }[];
  active: T;
  onChange: (key: T) => void;
}) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {tabs.map((tab) => {
        const selected = tab.key === active;
        const tone = tab.tone ?? "var(--primary)";
        return (
          <button
            key={tab.key}
            onClick={() => onChange(tab.key)}
            aria-pressed={selected}
            style={{
              padding: "8px 14px",
              borderRadius: 10,
              border: `1px solid ${selected ? tone : "var(--border)"}`,
              background: selected ? tone : "#fff",
              color: selected ? "#fff" : "var(--text-secondary)",
              fontWeight: 700,
              fontSize: 13,
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              transition: "all 0.15s",
            }}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span
                style={{
                  background: selected ? "rgba(255,255,255,0.24)" : "var(--bg)",
                  color: selected ? "#fff" : "var(--text-muted)",
                  borderRadius: 999,
                  padding: "1px 7px",
                  fontSize: 11,
                  fontWeight: 800,
                }}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function Pagination({
  offset,
  limit,
  total,
  onChange,
  busy,
}: {
  offset: number;
  limit: number;
  total: number;
  onChange: (offset: number) => void;
  busy?: boolean;
}) {
  if (total === 0) return null;

  const from = offset + 1;
  const to = Math.min(offset + limit, total);
  const canPrev = offset > 0;
  const canNext = to < total;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        marginTop: 16,
        flexWrap: "wrap",
      }}
    >
      <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
        Showing <strong style={{ color: "var(--text-secondary)" }}>{from}–{to}</strong> of{" "}
        <strong style={{ color: "var(--text-secondary)" }}>{total.toLocaleString()}</strong>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          className="btn btn-sm"
          disabled={!canPrev || busy}
          onClick={() => onChange(Math.max(0, offset - limit))}
          style={{ opacity: canPrev && !busy ? 1 : 0.45 }}
        >
          <ChevronLeft size={14} /> Previous
        </button>
        <button
          className="btn btn-sm"
          disabled={!canNext || busy}
          onClick={() => onChange(offset + limit)}
          style={{ opacity: canNext && !busy ? 1 : 0.45 }}
        >
          Next <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

/**
 * Search input that doesn't fire a request per keystroke.
 *
 * Search moved server-side with pagination, so the debounce is load-bearing
 * now rather than a nicety.
 */
export function useDebounced<T>(value: T, delay = 320): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function Avatar({
  name,
  url,
  size = 34,
  seed,
}: {
  name: string | null;
  url?: string | null;
  size?: number;
  seed?: string;
}) {
  const initials =
    name
      ?.split(" ")
      .map((part) => part[0])
      .filter(Boolean)
      .join("")
      .toUpperCase()
      .slice(0, 2) || "?";

  const palette = ["#6c5ffc", "#f16d75", "#19b159", "#f7b731", "#4ec2f0", "#a855f7"];
  const key = seed ?? name ?? "?";
  // Sum rather than charCodeAt(0): the old version keyed off the first
  // character of a uuid, and uuids skew heavily toward a few leading hex
  // digits, so most avatars came out the same colour.
  const hash = Array.from(key).reduce((sum, char) => sum + char.charCodeAt(0), 0);
  const background = palette[hash % palette.length];

  if (url) {
    return (
      <img
        src={url}
        alt=""
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          objectFit: "cover",
          flexShrink: 0,
          border: "1px solid var(--border)",
        }}
      />
    );
  }

  return (
    <div
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background,
        color: "#fff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.36,
        fontWeight: 800,
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
}
